import React from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import {
  DIALOG_HEADER_PX,
  DIALOG_HEADER_PY,
} from '../../../theme/dialogs'
import { MODAL_VARIANTS } from './modalVariants'
import type { ModalVariant } from './modalVariants'

/**
 * Header block for the shared dialog shell.
 *
 * Deliberately NOT shared with `AppDrawerHeader` - a dialog and a drawer
 * are structurally different containers and `AppDrawer/*` is out of scope
 * for the dialog consolidation. This component mirrors `AppDrawerHeader`'s
 * token *values* (px:2 py:1.25; eyebrow overline 0.65rem/0.08em/700;
 * title H6 1.05rem/700/1.25; subtitle caption text.secondary) but owns its
 * own JSX and adds a leading icon, a right-side adornment slot, and an
 * optional close affordance the drawer header lacks.
 *
 * Note: the theme `h6` colour is teal `#007f98`. A dialog header reads
 * calmer with a neutral title, so the title here EXPLICITLY sets
 * `color: 'text.primary'` (the conscious divergence from the drawer).
 */
export interface DialogHeaderProps {
  /** Overline shown above the title. */
  eyebrow?: string
  title: React.ReactNode
  /** `title` attribute for hover text when the title is truncated. */
  titleAttr?: string
  subtitle?: React.ReactNode
  /** Drives the leading icon + colour, rendered in a soft tinted badge. This is
   *  the standard way to give a header its icon - pick a variant, never a raw
   *  icon. Defaults to `info` (set by `BaseDialog`). */
  variant?: ModalVariant
  /** Escape hatch: a caller-supplied icon node rendered as-is, in place of the
   *  variant badge. Reserved for the rare header that needs a non-taxonomy icon;
   *  prefer `variant`. */
  leadingIcon?: React.ReactNode
  /** Right-aligned content (chips, toggles) shown on the title row. */
  adornment?: React.ReactNode
  /** When provided (and `dismissable`), renders a close icon button. */
  onClose?: () => void
  /** Controls whether the close icon renders. Defaults to true. */
  dismissable?: boolean
  sx?: SxProps<Theme>
}

const DialogHeader: React.FC<DialogHeaderProps> = ({
  eyebrow,
  title,
  titleAttr,
  subtitle,
  variant,
  leadingIcon,
  adornment,
  onClose,
  dismissable = true,
  sx,
}) => {
  const showClose = dismissable && typeof onClose === 'function'

  // The leading icon: a caller-supplied node wins (rare escape hatch), else the
  // variant resolves to a single icon inside a soft tinted badge so every header
  // reads the same way.
  const variantSpec = variant ? MODAL_VARIANTS[variant] : null
  const VariantIcon = variantSpec?.icon
  const renderedLeading =
    leadingIcon != null ? (
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {leadingIcon}
      </Box>
    ) : variantSpec && VariantIcon ? (
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(variantSpec.color, 0.12),
        }}
      >
        <VariantIcon sx={{ color: variantSpec.color, fontSize: 24 }} />
      </Box>
    ) : null

  return (
    <Box
      sx={{
        px: DIALOG_HEADER_PX,
        py: DIALOG_HEADER_PY,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        {renderedLeading}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
                fontWeight: 700,
                color: 'text.secondary',
                // lineHeight 1 strips the overline's own half-leading, so the
                // eyebrow->title gap is just this `mb` plus the title's
                // intrinsic top leading - ~60% tighter than the default rhythm.
                // This `mb` is the single knob for the gap.
                lineHeight: 1,
                mb: 0.1,
              }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            variant="h6"
            component="h2"
            title={titleAttr}
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              lineHeight: 1.25,
              // Theme h6 is teal; dialogs read calmer with a neutral title.
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              mt: 0,
            }}
          >
            {title}
          </Typography>
          {subtitle != null &&
            // The visible title->subtitle gap is mostly line-box leading (the
            // title carries ~2px of bottom leading, the subtitle ~1.6px of top
            // leading), not the margin. So the subtitle sits directly under the
            // title (no extra `mt`) with a slightly tightened `lineHeight` -
            // tighter than the original airy rhythm, but not cramped. Scoped to
            // the subtitle element, so single-line / title-only headers are
            // untouched (no subtitle to shift, and the title's own lineHeight
            // stays 1.25 so its descenders never clip under `overflow: hidden`).
            (typeof subtitle === 'string' ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0, fontSize: '0.78rem', lineHeight: 1.25 }}
              >
                {subtitle}
              </Typography>
            ) : (
              <Box sx={{ mt: 0 }}>{subtitle}</Box>
            ))}
        </Box>
        {adornment != null && (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
            {adornment}
          </Box>
        )}
        {showClose && (
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{ flexShrink: 0, mr: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Box>
  )
}

export default DialogHeader
